/**
 * Secure auth storage utility.
 *
 * Sprint 6 update (2026-02-15) — switched from `sessionStorage` (cleared on
 * tab close, ~1h effective) to `localStorage` with a 24h TTL stamp. PhD
 * candidates who close the laptop and reopen the next morning should NOT
 * be forced to log in again.
 *
 * Security trade-off: localStorage is XSS-readable. We mitigate by:
 *   - storing only an opaque session marker + serialised user profile
 *     (no auth token / password / PHI)
 *   - enforcing an explicit `expires_at` timestamp so stale sessions are
 *     refused even if the storage row is replayed
 *   - re-checking expiration on every getter, not just on app boot
 */

const STORAGE = typeof window !== 'undefined' ? window.localStorage : null;
const SESSION_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

function _now() {
  return Date.now();
}

/** Returns true if the row exists AND has not expired. Clears expired rows. */
function _stillValid(prefix) {
  if (!STORAGE) return false;
  const exp = parseInt(STORAGE.getItem(`${prefix}_expires_at`) || '0', 10);
  if (!exp) return false;
  if (_now() > exp) {
    // expired — clean up so subsequent reads behave consistently
    STORAGE.removeItem(`${prefix}_authenticated`);
    STORAGE.removeItem(`${prefix}_user`);
    STORAGE.removeItem(`${prefix}_email`);
    STORAGE.removeItem(`${prefix}_token`);
    STORAGE.removeItem(`${prefix}_expires_at`);
    return false;
  }
  return true;
}

function _stamp(prefix) {
  if (!STORAGE) return;
  STORAGE.setItem(`${prefix}_expires_at`, String(_now() + SESSION_TTL_MS));
}

export const authStorage = {
  // Author auth
  setAuthorAuth(user, token) {
    if (!STORAGE) return;
    STORAGE.setItem('author_authenticated', 'true');
    STORAGE.setItem('author_user', JSON.stringify(user));
    if (token) STORAGE.setItem('author_token', token);
    _stamp('author');
  },
  getAuthorToken() {
    if (!_stillValid('author')) return null;
    return STORAGE.getItem('author_token');
  },
  getAuthorUser() {
    if (!_stillValid('author')) return null;
    try {
      const data = STORAGE.getItem('author_user');
      return data ? JSON.parse(data) : null;
    } catch {
      return null;
    }
  },
  isAuthorAuthenticated() {
    if (!_stillValid('author')) return false;
    return STORAGE.getItem('author_authenticated') === 'true';
  },
  clearAuthorAuth() {
    if (!STORAGE) return;
    STORAGE.removeItem('author_authenticated');
    STORAGE.removeItem('author_user');
    STORAGE.removeItem('author_token');
    STORAGE.removeItem('author_expires_at');
  },
  updateAuthorUser(user) {
    if (!STORAGE) return;
    STORAGE.setItem('author_user', JSON.stringify(user));
    _stamp('author'); // sliding window — touching the profile keeps the session warm
  },

  // Admin auth
  setAdminAuth(email) {
    if (!STORAGE) return;
    STORAGE.setItem('admin_authenticated', 'true');
    STORAGE.setItem('admin_email', email);
    _stamp('admin');
  },
  isAdminAuthenticated() {
    if (!_stillValid('admin')) return false;
    return STORAGE.getItem('admin_authenticated') === 'true';
  },
  getAdminEmail() {
    if (!_stillValid('admin')) return null;
    return STORAGE.getItem('admin_email');
  },
  clearAdminAuth() {
    if (!STORAGE) return;
    STORAGE.removeItem('admin_authenticated');
    STORAGE.removeItem('admin_email');
    STORAGE.removeItem('admin_expires_at');
  },
};
